import { useCallback, useEffect, useRef, useState } from 'react';
import type { RecordingSession } from './useRecordingSession';
import {
  MAX_DURATION_EXTEND_MS,
  MAX_DURATION_MS,
  evaluateAutoStop,
  type AutoStopReason,
} from './autoStopRules';

export { autoStopReasonText, type AutoStopReason } from './autoStopRules';

/**
 * Otomatik durdurma. Eskiden kayıt yalnızca "Bitir & Özetle"ye basılınca
 * bitiyordu: toplantı kapanıp kullanıcı uygulamayı unuttuğunda kayıt saatlerce
 * boş odayı dinlemeye devam ediyordu.
 *
 * Kurallar autoStopRules.ts'te. Hiçbir kural kaydı doğrudan kesmiyor:
 * önce geri sayımlı bir uyarı çıkıyor, kullanıcı "devam" diyebiliyor, ses geri
 * gelirse uyarı kendiliğinden kalkıyor.
 */

/** Uyarıdan kaydın bitirilmesine kadar geçen süre. */
export const AUTO_STOP_COUNTDOWN_S = 60;

const TICK_MS = 1000;

export interface AutoStopWarning {
  reason: AutoStopReason;
  remainingSeconds: number;
}

export interface AutoStopOptions {
  /** Ayarlar > Takvim > "Görüşme bitince kaydı durdur". Kapalıyken yalnızca üst süre sınırı. */
  callEndRules: boolean;
}

export function useAutoStop(session: RecordingSession, onAutoStop: () => void, options: AutoStopOptions) {
  const [warning, setWarning] = useState<AutoStopWarning | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  /** Görüşme uygulaması mikrofonu bıraktığı an — ana süreç bildiriyor (Windows). */
  const callEndedAtRef = useRef<number | null>(null);

  // Aralık içinden okunan her şey ref'te — aralık her render'da yeniden
  // kurulmasın, geri sayım sıfırlanmasın.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const onAutoStopRef = useRef(onAutoStop);
  onAutoStopRef.current = onAutoStop;

  const liveSinceRef = useRef<number | null>(null);
  const maxDurationRef = useRef(MAX_DURATION_MS);
  /** "Devam et"ten sonra sessizlik baştan birikmeli — hemen yeniden uyarmasın. */
  const snoozedAtRef = useRef(0);
  const deadlineRef = useRef<number | null>(null);
  const reasonRef = useRef<AutoStopReason | null>(null);

  const clearWarning = useCallback(() => {
    deadlineRef.current = null;
    reasonRef.current = null;
    setWarning(null);
  }, []);

  const isLive = session.phase === 'live';

  useEffect(() => {
    return window.zabit.onCallState((state) => {
      callEndedAtRef.current = state.endedAt;
    });
  }, []);

  // Kayıt canlıya geçtiğinde sayaçları kur, bittiğinde her şeyi sıfırla.
  useEffect(() => {
    if (!isLive) {
      liveSinceRef.current = null;
      clearWarning();
      return;
    }
    const now = Date.now();
    liveSinceRef.current = now;
    maxDurationRef.current = MAX_DURATION_MS;
    snoozedAtRef.current = now;
    callEndedAtRef.current = null;
  }, [isLive, clearWarning]);

  useEffect(() => {
    if (!isLive) return;

    const evaluate = (now: number): AutoStopReason | null => {
      const current = sessionRef.current;
      return evaluateAutoStop({
        now,
        liveSince: liveSinceRef.current ?? now,
        maxDurationMs: maxDurationRef.current,
        isPaused: current.isPaused,
        snoozedAt: snoozedAtRef.current,
        silence: current.getSilence(),
        scheduledEndAt: current.scheduledEndAt,
        callEndRules: optionsRef.current.callEndRules,
        callEndedAt: callEndedAtRef.current,
      });
    };

    const tick = () => {
      const now = Date.now();
      const reason = evaluate(now);

      if (deadlineRef.current === null) {
        if (!reason) return;
        deadlineRef.current = now + AUTO_STOP_COUNTDOWN_S * 1000;
        reasonRef.current = reason;
        setWarning({ reason, remainingSeconds: AUTO_STOP_COUNTDOWN_S });
        return;
      }

      // Ses geri geldiyse (kural artık tutmuyorsa) uyarıyı kaldır.
      if (!reason) {
        clearWarning();
        return;
      }

      const remainingMs = deadlineRef.current - now;
      if (remainingMs <= 0) {
        clearWarning();
        onAutoStopRef.current();
        return;
      }

      reasonRef.current = reason;
      setWarning({ reason, remainingSeconds: Math.ceil(remainingMs / 1000) });
    };

    const id = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(id);
  }, [isLive, clearWarning]);

  /** Uyarıyı kapatır; sessizlik kuralları sıfırdan birikir, üst sınır uzar. */
  const keepRecording = useCallback(() => {
    const now = Date.now();
    snoozedAtRef.current = now;
    if (reasonRef.current === 'max-duration' && liveSinceRef.current !== null) {
      maxDurationRef.current = now - liveSinceRef.current + MAX_DURATION_EXTEND_MS;
    }
    clearWarning();
  }, [clearWarning]);

  const stopNow = useCallback(() => {
    clearWarning();
    onAutoStopRef.current();
  }, [clearWarning]);

  return { warning, keepRecording, stopNow };
}
